/**
 * PromemoriaPanel — pannello dei promemoria integrato nel Centro Comunicazioni
 * Il Diario di Bordo - Frontend
 *
 * Estratto da pages/RemindersPage.jsx. Si comporta in modo autonomo, senza
 * props richieste: lista promemoria + form modale di creazione.
 */

import { useState, useEffect, useCallback } from 'react'
import { Calendar, Plus, Check, Trash2, Repeat, X, Lock, Users, UserPlus } from 'lucide-react'
import { remindersApi, usersApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

const RECURRENCE_PRESETS = [
  { key: 'none', label: 'Una volta', rrule: null },
  { key: 'daily', label: 'Ogni giorno', rrule: 'FREQ=DAILY' },
  { key: 'weekly', label: 'Ogni settimana', rrule: 'FREQ=WEEKLY' },
  { key: 'monthly', label: 'Ogni mese', rrule: 'FREQ=MONTHLY' }
]

export default function PromemoriaPanel() {
  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await remindersApi.list()
      setReminders(data?.data || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleComplete = async (id) => {
    await remindersApi.complete(id)
    load()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo promemoria?')) return
    await remindersApi.remove(id)
    load()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Crea promemoria personali una tantum o ricorrenti.
        </p>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 text-sm"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Nuovo</span>
        </button>
      </div>

      {showForm && (
        <ReminderForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load() }}
        />
      )}

      {error && (
        <p className="text-sm text-red-600 mb-4">{error}</p>
      )}

      {loading && reminders.length === 0 && <p className="text-gray-500">Caricamento...</p>}
      {!loading && reminders.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>Nessun promemoria</p>
        </div>
      )}

      <ul className="space-y-2">
        {reminders.map((r) => {
          const isOverdue = !r.completed && new Date(r.scheduledAt) < new Date()
          return (
            <li
              key={r._id}
              className={`p-4 rounded-lg border bg-white dark:bg-gray-800 flex items-start gap-3 ${
                isOverdue
                  ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <button
                onClick={() => handleComplete(r._id)}
                className="mt-1 w-5 h-5 rounded-full border-2 border-gray-300 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center justify-center flex-shrink-0"
                aria-label="Completa"
              >
                <Check className="h-3 w-3 text-transparent hover:text-primary-500" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-900 dark:text-white">{r.title}</p>
                  {isOverdue && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      Scaduto
                    </span>
                  )}
                  {r.rrule && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center gap-1">
                      <Repeat className="h-3 w-3" />
                      {humanizeRrule(r.rrule)}
                    </span>
                  )}
                  <VisibilityChip visibility={r.visibility} assignedCount={r.assignedToUserIds?.length || 0} />
                </div>
                {r.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{r.description}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(r.scheduledAt).toLocaleString('it-IT', {
                    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
              <button
                onClick={() => handleDelete(r._id)}
                className="text-gray-400 hover:text-red-500 p-1"
                aria-label="Elimina"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function humanizeRrule(rrule) {
  const preset = RECURRENCE_PRESETS.find((p) => p.rrule === rrule)
  if (preset) return preset.label.toLowerCase()
  return 'ricorrente'
}

function VisibilityChip({ visibility, assignedCount = 0 }) {
  if (!visibility || visibility === 'private') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 flex items-center gap-1">
        <Lock className="h-3 w-3" />
        Privato
      </span>
    )
  }
  if (visibility === 'tenant') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex items-center gap-1">
        <Users className="h-3 w-3" />
        Tutto il centro
      </span>
    )
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 flex items-center gap-1">
      <UserPlus className="h-3 w-3" />
      {assignedCount} utent{assignedCount === 1 ? 'e' : 'i'}
    </span>
  )
}

function ReminderForm({ onClose, onSaved }) {
  const { user } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState(() => {
    const d = new Date()
    d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 5))
    return d.toISOString().slice(0, 16)
  })
  const [recurrence, setRecurrence] = useState('none')
  const [visibility, setVisibility] = useState('private')
  const [assignedIds, setAssignedIds] = useState([])
  const [centerUsers, setCenterUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Carica utenti del centro solo quando l'utente apre la sezione "specifici"
  useEffect(() => {
    if (visibility !== 'specific' || centerUsers.length > 0) return
    setLoadingUsers(true)
    usersApi.list({ attivo: 'true' })
      .then(({ data }) => {
        const list = data?.data?.users || data?.data || []
        const myId = String(user?._id || user?.id || '')
        setCenterUsers(list.filter((u) => String(u._id) !== myId))
      })
      .catch(() => setCenterUsers([]))
      .finally(() => setLoadingUsers(false))
  }, [visibility, centerUsers.length, user])

  const toggleAssigned = (id) => {
    setAssignedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!title.trim()) return
    if (visibility === 'specific' && assignedIds.length === 0) {
      setError('Seleziona almeno un utente o cambia visibilità')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const preset = RECURRENCE_PRESETS.find((p) => p.key === recurrence)
      await remindersApi.create({
        title: title.trim(),
        description: description.trim() || undefined,
        scheduledAt: new Date(date).toISOString(),
        rrule: preset?.rrule || null,
        visibility,
        assignedToUserIds: visibility === 'specific' ? assignedIds : []
      })
      onSaved()
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nuovo promemoria</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Titolo</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Es. Controllare PEP di Mario"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Descrizione (opzionale)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quando</span>
            <input
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </label>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ripetizione</legend>
            <div className="grid grid-cols-2 gap-2">
              {RECURRENCE_PRESETS.map((p) => (
                <label
                  key={p.key}
                  className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer ${
                    recurrence === p.key
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="recurrence"
                    value={p.key}
                    checked={recurrence === p.key}
                    onChange={() => setRecurrence(p.key)}
                    className="sr-only"
                  />
                  <span className="text-sm">{p.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Visibilità</legend>
            <div className="space-y-2">
              {[
                { key: 'private', label: 'Solo per me', desc: 'Riservato al tuo account', Icon: Lock },
                { key: 'tenant', label: 'Tutto il centro', desc: 'Visibile a tutti gli utenti del centro', Icon: Users },
                { key: 'specific', label: 'Utenti specifici', desc: 'Scegli a chi inviarlo', Icon: UserPlus }
              ].map(({ key, label, desc, Icon }) => (
                <label
                  key={key}
                  className={`flex items-start gap-2 px-3 py-2 border rounded-lg cursor-pointer ${
                    visibility === key
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={key}
                    checked={visibility === key}
                    onChange={() => { setVisibility(key); if (key !== 'specific') setAssignedIds([]) }}
                    className="sr-only"
                  />
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${visibility === key ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {visibility === 'specific' && (
              <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                {loadingUsers && <p className="p-3 text-sm text-gray-500">Caricamento utenti...</p>}
                {!loadingUsers && centerUsers.length === 0 && (
                  <p className="p-3 text-sm text-gray-500">Nessun altro utente disponibile</p>
                )}
                <ul>
                  {centerUsers.map((u) => {
                    const checked = assignedIds.includes(u._id)
                    return (
                      <li key={u._id}>
                        <label className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                          checked ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssigned(u._id)}
                            className="rounded text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {u.nome} {u.cognome}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                            {u.role}
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </fieldset>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Salvataggio...' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
